import type { FC, ReactNode } from 'react'

import { useEffect, useState } from 'react'
import Link from 'next/link'
import { useRouter } from 'next/router'
import { Menu, Search, X } from 'lucide-react'
import { cn } from '@/lib/utils'
import { Container } from '@/components/generals/container'
import { Logo } from '@/components/generals/logo'

import { mainNav } from '@/constants/site'

export const SiteHeader: FC = (): ReactNode => {
    const router = useRouter()
    const [isMenuOpen, setIsMenuOpen] = useState(false)

    useEffect(() => {
        const closeMenu = () => setIsMenuOpen(false)

        router.events.on('routeChangeStart', closeMenu)

        return () => router.events.off('routeChangeStart', closeMenu)
    }, [router.events])

    useEffect(() => {
        if (!isMenuOpen) return

        const handleKeyDown = (event: KeyboardEvent) => {
            if (event.key === 'Escape') setIsMenuOpen(false)
        }

        window.addEventListener('keydown', handleKeyDown)

        return () => window.removeEventListener('keydown', handleKeyDown)
    }, [isMenuOpen])

    const isActive = (href: string): boolean => router.pathname === href || router.pathname.startsWith(`${href}/`)

    return (
        <header className='sticky top-0 z-40 border-b border-border/80 bg-background/90 backdrop-blur-md supports-[backdrop-filter]:bg-background/75'>
            <Container className='flex h-16 items-center justify-between gap-6 sm:h-[4.5rem]'>
                <Logo />

                <nav aria-label='Main' className='hidden md:block'>
                    <ul className='flex items-center gap-1'>
                        {mainNav.map(item => (
                            <li key={item.href}>
                                <Link
                                    href={item.href}
                                    aria-current={isActive(item.href) ? 'page' : undefined}
                                    className={cn(
                                        'inline-flex h-10 items-center rounded-full px-4 text-[0.95rem] font-medium transition-colors focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-ring',
                                        isActive(item.href)
                                            ? 'bg-accent text-foreground'
                                            : 'text-muted-foreground hover:text-foreground'
                                    )}
                                >
                                    {item.label}
                                </Link>
                            </li>
                        ))}
                    </ul>
                </nav>

                <div className='flex items-center gap-1.5'>
                    <Link
                        href='/search'
                        aria-label='Search stories'
                        className='inline-flex size-10 items-center justify-center rounded-full text-foreground/80 transition-colors hover:bg-accent hover:text-foreground focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-ring'
                    >
                        <Search className='size-5' aria-hidden='true' />
                    </Link>

                    <a
                        href='#newsletter'
                        className='hidden h-10 items-center rounded-full bg-primary px-5 text-sm font-medium text-primary-foreground transition-colors hover:bg-primary/85 focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-ring sm:inline-flex'
                    >
                        Subscribe
                    </a>

                    <button
                        type='button'
                        aria-expanded={isMenuOpen}
                        aria-controls='mobile-menu'
                        aria-label={isMenuOpen ? 'Close menu' : 'Open menu'}
                        onClick={() => setIsMenuOpen(open => !open)}
                        className='inline-flex size-10 items-center justify-center rounded-full text-foreground transition-colors hover:bg-accent focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-ring md:hidden'
                    >
                        {isMenuOpen
                            ? <X className='size-5' aria-hidden='true' />
                            : <Menu className='size-5' aria-hidden='true' />}
                    </button>
                </div>
            </Container>

            <div
                id='mobile-menu'
                hidden={!isMenuOpen}
                className='border-t border-border bg-background md:hidden'
            >
                <Container className='pb-6 pt-3'>
                    <nav aria-label='Mobile'>
                        <ul className='flex flex-col divide-y divide-border'>
                            {mainNav.map(item => (
                                <li key={item.href}>
                                    <Link
                                        href={item.href}
                                        aria-current={isActive(item.href) ? 'page' : undefined}
                                        className={cn(
                                            'block py-3.5 font-heading text-2xl tracking-tight transition-colors',
                                            isActive(item.href) ? 'text-brand' : 'text-foreground hover:text-brand'
                                        )}
                                    >
                                        {item.label}
                                    </Link>
                                </li>
                            ))}
                        </ul>
                    </nav>

                    <a
                        href='#newsletter'
                        onClick={() => setIsMenuOpen(false)}
                        className='mt-5 flex h-12 items-center justify-center rounded-full bg-primary text-sm font-medium text-primary-foreground transition-colors hover:bg-primary/85'
                    >
                        Subscribe to the newsletter
                    </a>
                </Container>
            </div>
        </header>
    )
}
